import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {CheckboxAreaDirective, ReorderableRowGripComponent} from '@brunata-angular/ui';
import {TRANSLATE} from '@brunata-angular/util';
import {
    exportColumnHeadingBundle,
    OrderedExportColumn,
} from '../../shared/models/export-columns.model';

@Component({
    selector: '[app-export-column-row]',
    standalone: true,
    imports: [ReorderableRowGripComponent],
    hostDirectives: [
        {
            directive: CheckboxAreaDirective,
            inputs: ['appCheckboxAreaDisabled'],
        },
    ],
    template: `
        <td class="fw-bold text-center">
            @if (column().orderNumber !== null) {
                {{ column().orderNumber }}
            } @else {
                <span class="text-secondary" aria-hidden="true">–</span>
            }
        </td>
        <td>
            <app-reorderable-row-grip [label]="dragLabel()" />
        </td>
        <td>
            <div class="d-flex align-items-center gap-2">
                <input
                    type="checkbox"
                    class="form-check-input mt-0"
                    [id]="'export-column-' + column().id"
                    [checked]="column().included"
                    [disabled]="column().locked"
                    (change)="toggled.emit()" />
                <label
                    class="form-check-label mb-0"
                    [class.text-secondary]="!column().included"
                    [for]="'export-column-' + column().id">
                    {{ label() }}
                </label>
            </div>
        </td>
        <td class="header-cell">
            <input
                #headerInput
                type="text"
                class="form-control form-control-sm"
                [class.text-secondary]="!column().included"
                [value]="column().header ?? ''"
                [placeholder]="defaultHeader()"
                [disabled]="!column().included"
                [attr.aria-label]="headerLabel()"
                (input)="renamed.emit(headerInput.value)" />
        </td>
    `,
    styles: `
        .form-check-label {
            text-box-trim: trim-both;
            text-box-edge: cap alphabetic;
        }

        .header-cell {
            height: 1px;
            padding-block: 0;
        }

        .header-cell .form-control {
            height: 100%;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportColumnRowComponent {
    readonly column = input.required<OrderedExportColumn>();

    readonly toggled = output<void>();
    readonly renamed = output<string>();

    private readonly translate = inject(TRANSLATE);

    protected readonly label = computed(() =>
        this.translate(this.column().labelKey, 'online', 'export'),
    );

    protected readonly defaultHeader = computed(() =>
        this.translate(
            this.column().headingKey,
            exportColumnHeadingBundle.application,
            exportColumnHeadingBundle.module,
        ),
    );

    protected readonly headerLabel = computed(() =>
        this.translate('customizedexports_export_columns_header_aria', 'online', 'export', [
            this.label(),
        ]),
    );

    protected readonly dragLabel = computed(() =>
        this.translate('customizedexports_export_columns_drag_aria', 'online', 'export', [
            this.label(),
        ]),
    );
}
